import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Columns3, Download, FileText, RefreshCw, Save, SlidersHorizontal, Workflow } from 'lucide-react';
import PageHeader from '../../../components/layout/PageHeader';
import EventPlannerBoard from '../../../components/events/EventPlannerBoard';
import CardConfigSheet from '../../../components/events/CardConfigSheet';
import { CardConfigContext } from '../../../components/events/CardConfigContext';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  buildParticipants,
  enrichFieldMapping,
  mergeEventSlots,
  fetchEvent,
  loadInvitationWithResponses,
  reconcileEventWithResponses,
  saveEvent,
  syncKnownResponses,
  syncStoresSlotOrders,
} from '../../../lib/events';
import { exportEventToExcel, exportEventToPdf } from '../../../lib/eventExport';
import { toast } from '../../../lib/toast';
import ConfirmDialog from '../../../components/ui/confirm-dialog';
import { PlannerSkeleton, PAGE_SHELL_WIDE } from '../../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';

const EventPlannerFlow = lazy(() => import('../../../components/events/EventPlannerFlow'));

function useIsBelowLg() {
  const [isBelow, setIsBelow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const handleChange = (event) => setIsBelow(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isBelow;
}

function EventPlanner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [invitationName, setInvitationName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sourceData, setSourceData] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [plannerView, setPlannerView] = useState('columns');
  const [cardSheetOpen, setCardSheetOpen] = useState(false);
  const isBelowLg = useIsBelowLg();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const ev = await fetchEvent(id);
      if (!ev) {
        navigate('/eventi');
        return;
      }
      const data = await loadInvitationWithResponses(ev.sourceInvitationId);
      if (!data) {
        setEvent(ev);
        setParticipants([]);
        return;
      }
      setSourceData(data);
      setInvitationName(data.invitation.name || ev.sourceInvitationId);
      const mapping = enrichFieldMapping(ev.fieldMapping, data.fields);
      const parts = buildParticipants(data.responses, mapping, data.fields);
      setParticipants(parts);

      const slots = mergeEventSlots(ev, data.responses, mapping, data.fields);
      const synced = syncStoresSlotOrders({ ...ev, fieldMapping: mapping, slots });
      const { event: reconciled, removedCount, migratedCount } = reconcileEventWithResponses(
        synced,
        data.responses
      );
      setEvent(reconciled);
      if (removedCount > 0 || migratedCount > 0) {
        await saveEvent(reconciled);
        const parts = [];
        if (migratedCount > 0) parts.push(`${migratedCount} assegnazioni aggiornate`);
        if (removedCount > 0) parts.push(`${removedCount} assegnazioni obsolete rimosse`);
        toast.info('Piano aggiornato', parts.join(', ') + '.');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isMapFullscreen = plannerView === 'map' && !isBelowLg;

  useEffect(() => {
    if (!isMapFullscreen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMapFullscreen]);

  const handleSave = async () => {
    if (!event) return;
    setSaving(true);
    try {
      const toSave = sourceData
        ? reconcileEventWithResponses(event, sourceData.responses).event
        : event;
      await saveEvent(toSave);
      setEvent(toSave);
      toast.success('Piano salvato', 'Turni e assegnazioni aggiornati sul server.');
    } catch (e) {
      console.error(e);
      toast.error('Salvataggio non riuscito', 'Riprova tra qualche istante.');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    if (!event || !sourceData) return;
    setSyncing(true);
    try {
      const fresh = await loadInvitationWithResponses(event.sourceInvitationId);
      if (!fresh) return;
      const { event: updated, newResponseIds } = syncKnownResponses(event, fresh.responses);
      const slots = mergeEventSlots(updated, fresh.responses, event.fieldMapping, fresh.fields);
      const synced = syncStoresSlotOrders({ ...updated, slots });
      const { event: reconciled, removedCount, migratedCount } = reconcileEventWithResponses(
        synced,
        fresh.responses
      );
      setEvent(reconciled);
      setSourceData(fresh);
      setParticipants(buildParticipants(fresh.responses, event.fieldMapping, fresh.fields));
      const parts = [];
      if (newResponseIds.length) parts.push(`${newResponseIds.length} nuove risposte importate`);
      if (migratedCount > 0) parts.push(`${migratedCount} assegnazioni aggiornate`);
      if (removedCount > 0) parts.push(`${removedCount} assegnazioni obsolete rimosse`);
      const message = parts.length ? `${parts.join(', ')}.` : 'Nessuna nuova risposta da importare.';
      toast.info('Sincronizzazione completata', message);
      await saveEvent(reconciled);
    } catch (e) {
      console.error(e);
      toast.error('Sincronizzazione non riuscita', 'Riprova tra qualche istante.');
    } finally {
      setSyncing(false);
    }
  };

  const plannerActions = (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setCardSheetOpen(true)}>
        <SlidersHorizontal className="h-4 w-4" />
        Card
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setConfirmAction('sync')}
        disabled={syncing}
      >
        <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
        Sincronizza
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={async () => {
          try {
            await exportEventToExcel(event, participants);
            toast.success('Export completato', 'Il file Excel è stato scaricato.');
          } catch (error) {
            console.error(error);
            toast.error('Export non riuscito', 'Impossibile generare il file Excel.');
          }
        }}
      >
        <Download className="h-4 w-4" />
        Excel
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={async () => {
          try {
            await exportEventToPdf(event, participants);
            toast.success('Export completato', 'Il file PDF è stato scaricato.');
          } catch (error) {
            console.error(error);
            toast.error('Export non riuscito', 'Impossibile generare il file PDF.');
          }
        }}
      >
        <FileText className="h-4 w-4" />
        PDF
      </Button>
      <Button
        type="button"
        size="sm"
        onClick={() => setConfirmAction('save')}
        disabled={saving}
      >
        <Save className="h-4 w-4" />
        {saving ? 'Salvataggio…' : 'Salva'}
      </Button>
    </>
  );

  const handlePlannerViewChange = (value) => {
    if (value === 'map' && isBelowLg) return;
    setPlannerView(value);
  };

  const plannerModeTabsList = (
    <TabsList aria-label="Modalità planner" className={isMapFullscreen ? 'h-9' : undefined}>
      <TabsTrigger value="columns" className="gap-2">
        <Columns3 className="h-4 w-4" />
        Colonne
      </TabsTrigger>
      <TabsTrigger value="map" className="gap-2" disabled={isBelowLg}>
        <Workflow className="h-4 w-4" />
        Mappa
      </TabsTrigger>
    </TabsList>
  );

  if (loading || !event) {
    return (
      <div className={PAGE_SHELL_WIDE}>
        <PageHeader
          title="Planner evento"
          description="Caricamento in corso…"
          backTo="/eventi"
          backLabel="Torna agli eventi"
        />
        <PlannerSkeleton />
      </div>
    );
  }

  if (isMapFullscreen) {
    return (
      <CardConfigContext.Provider value={event.cardConfig}>
        <div className="fixed inset-0 z-[100] flex flex-col bg-[var(--ba-body-bg)]">
          <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-[var(--ba-surface-border)] bg-[var(--ba-surface-bg)] px-3 py-2 sm:px-4">
            <Link
              to="/eventi"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--ba-muted)] hover:text-brand-orange"
            >
              <ArrowLeft className="h-4 w-4" />
              Eventi
            </Link>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-[var(--ba-body-text)]">{event.name}</p>
              <p className="truncate text-[11px] ba-muted">{invitationName}</p>
            </div>
            <div className="hidden flex-wrap gap-1.5 sm:flex">
              <Badge variant="secondary" className="text-[10px]">
                {participants.length} partecipanti
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {event.stores.length} negozi
              </Badge>
            </div>
            <Tabs value={plannerView} onValueChange={handlePlannerViewChange}>
              {plannerModeTabsList}
            </Tabs>
            <div className="flex flex-wrap items-center gap-2">{plannerActions}</div>
          </header>
          <div className="min-h-0 flex-1 p-3 sm:p-4">
            <Suspense fallback={<PlannerSkeleton />}>
              <EventPlannerFlow
                event={event}
                setEvent={setEvent}
                participants={participants}
                fullscreen
              />
            </Suspense>
          </div>
        </div>


        <CardConfigSheet
          open={cardSheetOpen}
          onOpenChange={setCardSheetOpen}
          config={event.cardConfig}
          participants={participants}
          invitationFields={sourceData?.fields || []}
          fieldMapping={event.fieldMapping}
          onApply={(cardConfig) => {
            setEvent({ ...event, cardConfig });
            toast.info('Card aggiornate', 'Premi Salva per rendere permanente la modifica.');
          }}
        />

        <ConfirmDialog
          elevated
          open={confirmAction === 'save'}
          onOpenChange={(open) => !open && setConfirmAction(null)}
          title="Salvare le modifiche?"
          description="Il piano turni e le assegnazioni verranno aggiornati sul server."
          confirmLabel="Salva"
          onConfirm={handleSave}
        />

        <ConfirmDialog
          elevated
          open={confirmAction === 'sync'}
          onOpenChange={(open) => !open && setConfirmAction(null)}
          title="Sincronizzare le risposte?"
          description="Verranno importate le nuove risposte dall'invito collegato e il piano turni verrà salvato."
          confirmLabel="Sincronizza"
          onConfirm={handleSync}
        />
      </CardConfigContext.Provider>
    );
  }

  return (
    <CardConfigContext.Provider value={event.cardConfig}>
    <div className={PAGE_SHELL_WIDE}>
      <PageHeader
        title="Planner evento"
        description={`Organizza i turni per l'invito "${invitationName}".`}
        backTo="/eventi"
        backLabel="Torna agli eventi"
      />

      <div className="mb-4">
        <label className="ba-label">Nome evento</label>
        <Input
          value={event.name}
          onChange={(e) => setEvent({ ...event, name: e.target.value })}
          className="max-w-md"
        />
        <div className="mt-2 flex flex-wrap gap-2">
          <Badge variant="secondary">{participants.length} partecipanti</Badge>
          <Badge variant="outline">{event.stores.length} negozi</Badge>
          <Badge variant="outline">{event.slots?.length || 0} turni</Badge>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-end gap-2">
        {plannerActions}
      </div>

      <Tabs value={plannerView} onValueChange={handlePlannerViewChange}>
        {plannerModeTabsList}
        {isBelowLg && (
          <p className="mt-2 text-xs ba-muted">
            La vista Mappa è disponibile da schermi larghi (desktop). Usa Colonne su questo dispositivo.
          </p>
        )}
        <TabsContent value="columns">
          <EventPlannerBoard event={event} setEvent={setEvent} participants={participants} />
        </TabsContent>
      </Tabs>


      <CardConfigSheet
        open={cardSheetOpen}
        onOpenChange={setCardSheetOpen}
        config={event.cardConfig}
        participants={participants}
        invitationFields={sourceData?.fields || []}
        fieldMapping={event.fieldMapping}
        onApply={(cardConfig) => {
          setEvent({ ...event, cardConfig });
          toast.info('Card aggiornate', 'Premi Salva per rendere permanente la modifica.');
        }}
      />

      <ConfirmDialog
        open={confirmAction === 'save'}
        onOpenChange={(open) => !open && setConfirmAction(null)}
        title="Salvare le modifiche?"
        description="Il piano turni e le assegnazioni verranno aggiornati sul server."
        confirmLabel="Salva"
        onConfirm={handleSave}
      />

      <ConfirmDialog
        open={confirmAction === 'sync'}
        onOpenChange={(open) => !open && setConfirmAction(null)}
        title="Sincronizzare le risposte?"
        description="Verranno importate le nuove risposte dall'invito collegato e il piano turni verrà salvato."
        confirmLabel="Sincronizza"
        onConfirm={handleSync}
      />
    </div>
    </CardConfigContext.Provider>
  );
}

export default EventPlanner;
